// Room — the cinematic "you just walked into a department" primitive.
//
// Every top-level tab is a ROOM inside the AI HQ building. Each room
// has a distinct environment (cinematic backdrop), a numbered eyebrow
// (its floor in the building), an AI resident who greets the operator
// with a live quote, and a slot for the real work UI which floats as
// glass panels over the room.
//
// Design contract:
//   · The room OWNS the viewport — full-bleed backdrop, no white gutter.
//   · The header is the "you're here" cue: room #, title, one-line
//     purpose, live AI persona chip with real-data quote.
//   · Content renders as glass — smoked-dark panels the operator can
//     read on any environment.
//   · The backdrop is CSS/SVG art (no external images), warm-dark,
//     amber-accented, keyed to the floor's identity color.
//   · Reduced-motion clients get no camera drift, no shimmer, no pulse.
//
// Usage:
//   <Room floor="sales" number={2} title="Sales Floor"
//         tagline="Find. Score. Prioritize." personaQuote="I found 12 new leads.">
//     <YourExistingLeadsView/>
//   </Room>

import React from 'react';
import hq from './theme';
import RoomEnvironment from './RoomEnvironment';
import { ROOM_VIDEOS } from './roomVideos';

// Floor → roomVideos key (for the ambient video wash). Most floors line
// up 1:1; the ones that don't (retention, growth, aiworkforce → workforce)
// need an explicit hop so every Room-wrapped tab still gets a wash.
const FLOOR_VIDEO_KEY = {
  command:        'home',
  sales:          'leads',
  pipeline:       'pipeline',
  calls:          'calls',
  communications: 'messages',
  clients:        'clients',
  revenue:        'commissions',
  calendar:       'calendar',
  reports:        'reports',
  growth:         'reports',
  aiworkforce:    'workforce',
  compliance:     'compliance',
  settings:       'settings',
  retention:      'clients',
};

// Per-floor AI resident metadata. Keyed to hq.floors slug. If a floor
// isn't listed, no persona chip renders — the room still greets you
// with number + title.
// Canonical roster — matches the Command Center grid mock
// (docs/hq/command_center_grid_reference.md, 2026-07-17) and the
// AI Employee build order at docs/agent_build_order.md.
// LEDGER + ASCEND are stubbed today — Room shows the chip; the
// grid card adds the honest NOT YET ACTIVE label.
const RESIDENTS = {
  command:        { name: 'PILOT',   role: 'Onboarding & Guide AI' },
  sales:          { name: 'SCOUT',   role: 'Lead Intelligence AI' },
  pipeline:       { name: 'UPLIFT',  role: 'Sales Coach AI' },
  calls:          { name: 'NOVA',    role: 'Communications AI' },
  communications: { name: 'NUDGE',   role: 'Follow-Up AI' },
  clients:        { name: 'RETAIN',  role: 'Client Success AI' },
  revenue:        { name: 'LEDGER',  role: 'Finance AI' },
  calendar:       { name: 'NOVA',    role: 'Books from conversations' },
  reports:        { name: 'ASCEND',  role: 'Analytics AI' },
  growth:         { name: 'ASCEND',  role: 'Analytics AI' },
  aiworkforce:    { name: 'PILOT',   role: 'Your guide to the roster' },
  compliance:     { name: 'SHIELD',  role: 'Compliance AI' },
  settings:       { name: 'SENTRY',  role: 'Platform Watch AI' },
  retention:      { name: 'RETAIN',  role: 'Client Success AI' },
};

// Room number → the "floor" you're on inside the building. Used as
// the eyebrow number. Consistent across all rooms so the operator
// builds a mental map. Unknown floors render without a number.
const FLOOR_NUMBER = {
  command:        1,
  sales:          2,
  pipeline:       3,
  calls:          4,
  communications: 5,
  clients:        6,
  revenue:        7,
  calendar:       8,
  reports:        9,
  aiworkforce:   10,
  compliance:    11,
  settings:      12,
};

export default function Room({
  floor,
  number,               // override the floor's default number
  title,                // display title, e.g. 'Sales Floor'
  tagline,              // one-line room purpose
  personaQuote,         // live-data quote the resident says on entry
  hidePersona,          // optional: some rooms don't have a resident
  children,             // the actual work UI — renders inside the room
  contentMaxWidth,      // optional cap on content width; default 1600
}) {
  const meta   = hq.floors[floor] || hq.floors.command;
  const accent = meta.accent;
  const resident = RESIDENTS[floor] || null;
  const roomNumber = number ?? FLOOR_NUMBER[floor];
  const reduced  = React.useMemo(
    () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
    []
  );

  return (
    <div
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: '#050709',
        color: hq.color.text,
        overflow: 'hidden',
      }}
    >
      {/* Cinematic backdrop keyed to the floor's identity. Fixed under
          the content, gently animated in unless reduced-motion. Now the
          per-floor SVG is the keyed color wash + graceful fallback beneath
          the photoreal headquarters render below. */}
      <RoomEnvironment floor={floor} accent={accent} reduced={reduced} />

      {/* Per-room cinematic wash — every wrapped tab now renders its own
          ambient Kling clip at low opacity behind the content, so
          Communications Room feels like Communications, Revenue Vault
          feels like Revenue, etc. Falls back to the shared photoreal
          floor still when a floor has no video assigned or the client
          honors reduced-motion. Sits between the SVG accent (zIndex 0)
          and the glass content (zIndex 2). Presentation only. */}
      <RoomWash floor={floor} accent={accent} reduced={reduced} />

      {/* Amber top edge — the "engineered" seam that stitches every
          room together as one building. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 1,
          background: hq.surface.topEdge,
          opacity: 0.7,
          pointerEvents: 'none',
        }}
      />

      {/* Header — number, title, tagline. Serif for the room title,
          the "cinematic department statement" reserved for these
          moments only. Fades in on entry. */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '28px 40px 8px',
          animation: reduced ? 'none' : `room-enter ${hq.motion.arrival} both`,
        }}
      >
        <div
          style={{
            display: 'inline-flex', alignItems: 'baseline', gap: 14,
            paddingBottom: 6,
            borderBottom: `1px solid ${hq.color.line}`,
          }}
        >
          {roomNumber != null && (
            <span
              style={{
                fontFamily: hq.type.mono, fontSize: hq.type.scale.tiny,
                letterSpacing: '0.18em',
                color: accent, opacity: 0.9,
              }}
            >
              {String(roomNumber).padStart(2, '0')}
            </span>
          )}
          <h1
            style={{
              fontFamily: hq.type.serif,
              fontSize: hq.type.scale.h1,
              lineHeight: 1.05, margin: 0,
              color: hq.color.text,
              letterSpacing: '-0.01em',
              textWrap: 'balance',
            }}
          >
            {title}
          </h1>
        </div>
        {tagline && (
          <div
            style={{
              marginTop: 8,
              fontFamily: hq.type.body,
              fontSize: 14,
              color: hq.color.textSec,
              letterSpacing: '0.01em',
              maxWidth: 720,
            }}
          >
            {tagline}
          </div>
        )}
      </div>

      {/* Resident chip — the AI who lives on this floor, greeting the
          operator with a live-data quote. Anchors the room's identity
          without stealing focus. */}
      {resident && !hidePersona && (
        <div
          style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 12,
            padding: '4px 40px 12px',
            animation: reduced ? 'none' : `room-enter ${hq.motion.arrival} both`,
            animationDelay: '.15s',
          }}
        >
          <PersonaBadge accent={accent} name={resident.name} role={resident.role} />
          {personaQuote && (
            <div
              style={{
                background: hq.color.glass,
                border: `1px solid ${hq.color.line}`,
                borderRadius: 12,
                padding: '10px 14px',
                maxWidth: 520,
                fontSize: 13,
                lineHeight: 1.45,
                color: hq.color.text,
                backdropFilter: 'blur(10px)',
                boxShadow: hq.surface.shadow,
                position: 'relative',
              }}
            >
              {/* speech-tail */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: -6, bottom: 12,
                  width: 10, height: 10,
                  background: hq.color.glass,
                  borderLeft: `1px solid ${hq.color.line}`,
                  borderBottom: `1px solid ${hq.color.line}`,
                  transform: 'rotate(45deg)',
                }}
              />
              {personaQuote}
            </div>
          )}
        </div>
      )}

      {/* Work UI slot — the real functional content. Glass-treated
          via .room-glass class so any nested panel inherits the
          smoked-dark treatment without needing to know it's inside
          a Room. */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '18px 40px 96px',
          maxWidth: contentMaxWidth || 1600,
          margin: '0 auto',
        }}
        className="room-content"
      >
        {children}
      </div>

      {/* Local keyframes — kept inline so a Room can be dropped into
          any parent without a global stylesheet dependency. */}
      <style>{`
        @keyframes room-enter {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .room-content .bg-white,
        .room-content .bg-slate-50,
        .room-content .bg-slate-100 {
          background: ${hq.color.panel} !important;
          color: ${hq.color.text} !important;
          border-color: ${hq.color.line} !important;
        }
        .room-content .text-slate-900,
        .room-content .text-slate-800,
        .room-content .text-slate-700 {
          color: ${hq.color.text} !important;
        }
        .room-content .text-slate-600,
        .room-content .text-slate-500,
        .room-content .text-slate-400 {
          color: ${hq.color.textSec} !important;
        }
        .room-content .border-slate-200,
        .room-content .border-slate-300,
        .room-content .border-slate-100 {
          border-color: ${hq.color.line} !important;
        }
      `}</style>
    </div>
  );
}

// PersonaBadge — the AI resident chip. Portrait initial + name +
// role, with a soft live-pulse dot to signal they're on duty. No
// stock imagery — a color-tinted glyph keyed to the floor accent.
function PersonaBadge({ accent, name, role }) {
  const initial = String(name || '?').charAt(0).toUpperCase();
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        background: hq.color.panel,
        border: `1px solid ${hq.color.line}`,
        borderRadius: 999,
        padding: '6px 14px 6px 6px',
        backdropFilter: 'blur(10px)',
        boxShadow: hq.surface.glow(accent),
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: 32, height: 32, borderRadius: '50%',
          display: 'grid', placeItems: 'center',
          background: `radial-gradient(circle at 30% 25%, ${accent}, rgba(9,12,16,0.9) 80%)`,
          color: '#0b0e13',
          fontFamily: hq.type.display,
          fontWeight: 800,
          fontSize: 14,
          boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.14)`,
        }}
      >
        {initial}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span
          style={{
            fontFamily: hq.type.display,
            fontWeight: 800,
            fontSize: 12,
            letterSpacing: '0.14em',
            color: hq.color.text,
          }}
        >
          {name}
        </span>
        <span
          style={{
            fontFamily: hq.type.mono,
            fontSize: 9,
            letterSpacing: '0.12em',
            color: hq.color.textSec,
            textTransform: 'uppercase',
          }}
        >
          {role}
        </span>
      </div>
      <span
        aria-hidden="true"
        style={{
          width: 6, height: 6, borderRadius: 999,
          background: hq.color.ok,
          boxShadow: `0 0 8px ${hq.color.ok}`,
          marginLeft: 4,
        }}
      />
    </div>
  );
}


// RoomWash — the per-room cinematic wash. Kling video at low opacity,
// with a strong scrim so content sits over readable ground on any
// footage. Falls back to the shared photoreal floor still when the
// floor has no video assigned or reduced-motion is active.
function RoomWash({ floor, accent, reduced }) {
  const videoKey = FLOOR_VIDEO_KEY[floor];
  const room = videoKey && ROOM_VIDEOS[videoKey];
  const videoUrl = room?.videoUrl;

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Fallback still: always paints, so we never flash black while a
          video buffers. Same shared render every floor picks up when
          it doesn't have a dedicated clip. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(/hq/hq-floor.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          opacity: 0.55,
        }}
      />
      {/* Cinematic clip on top — the "this room is alive" cue. */}
      {videoUrl && !reduced && (
        <video
          src={videoUrl}
          autoPlay muted loop playsInline preload="metadata"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            opacity: 0.32,
            filter: 'saturate(1.08) blur(0.4px)',
          }}
        />
      )}
      {/* Scrim: strong at top + bottom so content is always legible, a
          warm accent-tinted glow up top right. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            `linear-gradient(180deg, rgba(6,7,9,0.62) 0%, rgba(7,8,10,0.36) 42%, rgba(6,7,9,0.78) 100%),` +
            ` radial-gradient(1200px 520px at 82% 0%, ${withAlpha(accent, 0.14)}, transparent 70%)`,
        }}
      />
    </div>
  );
}

function withAlpha(hex, a) {
  if (!hex) return `rgba(245,166,35,${a})`;
  if (hex.startsWith('rgba') || hex.startsWith('rgb')) return hex;
  const c = hex.replace('#', '');
  const norm = c.length === 3 ? c.split('').map(x => x + x).join('') : c;
  const r = parseInt(norm.slice(0, 2), 16);
  const g = parseInt(norm.slice(2, 4), 16);
  const b = parseInt(norm.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
